import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { RecordCard } from '@/components/ui/RecordCard';
import { Loader2 } from 'lucide-react';
import { useNavigate, Link } from 'react-router';

// Define the record type
interface Record {
  id: string;
  englishName: string | null;
  scientificName: string | null;
  orgName: string | null;
  orgId: string | null;
  dtm: string;
  count: number | null;
  lat: number | null;
  lng: number | null;
  note: string | null;
  locationNote: string | null;
  createdAt: string;
  updatedAt: string;
}[]

export const LatestRecords: React.FC = ({ orgId }: { orgId: string }) => {


  // Fetch records
  const { data: records, isLoading, error } = useQuery({
    queryKey: ['records'],
    queryFn: async () => {
      const response = await fetch('/api/me/records');
      if (!response.ok) {
        throw new Error('Failed to fetch records');
      }
      return response.json() as Promise<Record[]>;
    },
  });

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const navigate = useNavigate();

  return (
    <div className="mx-auto ">
      <h2 className='text-xl font-bold tracking-tight mb-4 '>Latest Records</h2>

      {isLoading ? (
        <div className="flex justify-center p-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          Failed to load records. Please try again.
        </div>
      ) : (
        <>
          {/* Card layout for mobile */}
          <div className="block md:hidden space-y-4">
            {records && records.length > 0 ? (
              records.map((record) => (
                <RecordCard
                  key={record.id}
                  record={record}
                  onClick={() => navigate(`/app/org/${record.orgId}/record/${record.id}`)}
                />
              ))
            ) : (
              <div className="text-center py-4">No records found</div>
            )}
          </div>
          {/* Table layout for md+ */}
          <div className="hidden md:block overflow-auto max-h-[80vh]">
            {records && records.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map((record) => (
                    <TableRow key={record.id} onClick={() => navigate(`/app/org/${record.orgId}/record/${record.id}`)} className="cursor-pointer">
                      <TableCell>
                        <div className="font-medium">{record.englishName || 'Unknown Species'}</div>
                        <div className="text-sm italic text-muted-foreground">{record.scientificName}</div>
                      </TableCell>
                      <TableCell>
                        {record.orgId ? (
                          <Link to={`/app/org/${record.orgId}`} onClick={(e) => e.stopPropagation()}>
                            <Badge className="cursor-pointer">{record.orgName || 'Unknown'}</Badge>
                          </Link>
                        ) : (
                          <Badge>{record.orgName || 'Unknown'}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-muted-foreground line-clamp-2">{record.note || '-'}</p>
                        {record.locationNote && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{record.locationNote}</p>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDate(record.dtm)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-4">No records found</div>
            )}
          </div>
        </> // Added closing JSX fragment
      )}
    </div>
  );
};
